import { AnimatePresence, motion } from "motion/react"
import { useEffect, useRef, useState } from "react"

import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { type AttendanceStatus } from "#customTypes/types"

import AttendanceButton from "./AttendanceButton"

const BUTTON_SIZE = {
  small: 21,
  medium: 32,
  large: 48,
}

const GAP = 4
const ANIMATION_DURATION = 0.3

interface AttendanceStatusSelectorProps {
  status: AttendanceStatus
  onStatusChange: (newStatus: AttendanceStatus) => void
  size?: "small" | "medium" | "large"
  disabled?: boolean
}

// Define the status groups
const getStatusGroup = (status: AttendanceStatus) => {
  if (!status) return "invalid"

  if (status.startsWith("TRYOUT_")) return "tryout"
  if (status.startsWith("MAKEUP_")) return "makeup"
  if (status === ATTENDANCE_STATUSES.PLANNED_ABSENCE) return "planned_absence"
  return "normal"
}

const getStatusOptions = (status: AttendanceStatus): AttendanceStatus[] => {
  const group = getStatusGroup(status)

  switch (group) {
    case "tryout":
      return [
        ATTENDANCE_STATUSES.TRYOUT_PRESENT,
        ATTENDANCE_STATUSES.TRYOUT_ABSENT,
        ATTENDANCE_STATUSES.TRYOUT_TARDY,
      ]
    case "makeup":
      return [
        ATTENDANCE_STATUSES.MAKEUP_PRESENT,
        ATTENDANCE_STATUSES.MAKEUP_ABSENT,
        ATTENDANCE_STATUSES.MAKEUP_TARDY,
      ]
    case "planned_absence":
      return [ATTENDANCE_STATUSES.PLANNED_ABSENCE]
    default:
      return [
        ATTENDANCE_STATUSES.PRESENT,
        ATTENDANCE_STATUSES.ABSENT,
        ATTENDANCE_STATUSES.TARDY,
      ]
  }
}

// Determine initial expanded state based on status
const getInitialExpandedState = (status: AttendanceStatus) => {
  // If no status provided or status is any "enrolled" variant, start expanded
  if (
    !status ||
    status === ATTENDANCE_STATUSES.ENROLLED ||
    status === ATTENDANCE_STATUSES.TRYOUT_ENROLLED ||
    status === ATTENDANCE_STATUSES.MAKEUP_ENROLLED
  ) {
    return true
  }
  // If status is planned absence, start collapsed and non-clickable
  if (
    status === ATTENDANCE_STATUSES.PLANNED_ABSENCE ||
    status === ATTENDANCE_STATUSES.INVALID
  ) {
    return false
  }
  // If there's a selected status (present, absent, tardy), start collapsed
  return false
}

export const AttendanceStatusSelectorBar = ({
  status,
  onStatusChange,
  size = "medium",
  disabled = false,
}: AttendanceStatusSelectorProps) => {
  // React component key for stable animations
  const key = useRef<string>(Math.random().toString())

  const [isExpanded, setIsExpanded] = useState(getInitialExpandedState(status))
  const [isAnimating, setIsAnimating] = useState(false)
  const [optimisticStatus, setOptimisticStatus] =
    useState<AttendanceStatus>(status)

  const statusOptions = getStatusOptions(optimisticStatus)
  const isPlannedAbsence =
    optimisticStatus === ATTENDANCE_STATUSES.PLANNED_ABSENCE

  useEffect(() => {
    setOptimisticStatus(status)
  }, [status])

  const handleToggleExpanded = () => {
    if (!disabled && !isPlannedAbsence && !isAnimating) {
      setIsAnimating(true)
      setIsExpanded(!isExpanded)
    }
  }

  const handleAnimationComplete = () => {
    setIsAnimating(false)
  }

  const handleStatusChange = (newStatus: AttendanceStatus) => {
    setOptimisticStatus(newStatus)
    onStatusChange(newStatus)
  }

  const handleStatusClick = (newStatus: AttendanceStatus | null) => {
    if (isAnimating) return // Prevent clicks during animation
    handleToggleExpanded()

    if (newStatus === ATTENDANCE_STATUSES.INVALID) return

    if (newStatus !== null) {
      // From enrolled state: save the clicked status and collapse
      handleStatusChange(newStatus)
    } else {
      // From selected state: clear to enrolled and expand
      const group = getStatusGroup(optimisticStatus)
      let enrolledStatus: AttendanceStatus

      switch (group) {
        case "tryout":
          enrolledStatus = ATTENDANCE_STATUSES.TRYOUT_ENROLLED
          break
        case "makeup":
          enrolledStatus = ATTENDANCE_STATUSES.MAKEUP_ENROLLED
          break
        default:
          enrolledStatus = ATTENDANCE_STATUSES.ENROLLED
          break
      }

      handleStatusChange(enrolledStatus)
    }
  }

  const buttonWidth = BUTTON_SIZE[size]
  // For consistency this is set at the max count for any status group
  // Otherwise planned absences don't match the width of the other statuses
  const expandedWidth = 3 * (buttonWidth + GAP) - GAP
  // const expandedWidth = statusOptions.length * (buttonWidth + GAP) - GAP

  return (
    <motion.div
      style={{
        width: expandedWidth, // Always use expanded width
        // height: buttonWidth + 2,
        position: "relative",
        display: "flex",
        alignItems: "center",
      }}
    >
      <AnimatePresence mode="popLayout">
        {!isExpanded ? (
          // Collapsed state - single button centered
          <motion.div
            key={`collapsed-${key.current}`}
            initial={{ opacity: 0.5 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0.5 }}
            transition={{ duration: ANIMATION_DURATION - 0.1 }}
            onAnimationComplete={handleAnimationComplete}
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <AttendanceButton
              status={optimisticStatus}
              onClick={() => handleStatusClick(null)}
              disabled={disabled}
              size={size}
            />
          </motion.div>
        ) : (
          // Expanded state - multiple buttons spread across width
          <motion.div
            key={`expanded-${key.current}`}
            initial={{ opacity: 0.5 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0.5 }}
            transition={{ duration: ANIMATION_DURATION - 0.1, delay: 0.1 }}
            onAnimationComplete={handleAnimationComplete}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              gap: GAP,
            }}
          >
            {statusOptions.map((statusOption, index) => {
              // Calculate position relative to center for animation
              const centerIndex = (statusOptions.length - 1) / 2
              const offsetFromCenter = index - centerIndex

              return (
                <motion.div
                  key={`${statusOption}-${key.current}`}
                  initial={{
                    x: -offsetFromCenter * (buttonWidth + GAP), // Start from center position
                    opacity: 0.5,
                    scale: 0.8,
                  }}
                  animate={{
                    x: 0, // Move to final position
                    opacity: 1,
                    scale: 1,
                  }}
                  exit={{
                    x: -offsetFromCenter * (buttonWidth + GAP), // Slide back to center
                    opacity: 0.5,
                    scale: 0.8,
                  }}
                  transition={{
                    duration: ANIMATION_DURATION,
                    delay: Math.abs(offsetFromCenter) * 0.05, // Stagger based on distance from center
                    ease: "easeOut",
                  }}
                  style={{
                    position: "relative",
                  }}
                >
                  <AttendanceButton
                    status={statusOption}
                    onClick={() => handleStatusClick(statusOption)}
                    size={size}
                  />
                </motion.div>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
